import type { TableSortingState } from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PhraseSearchRole } from '@/entities/search';
import type { ContentSearchMode } from '@/entities/settings';
import type { TableContentSearchState, TableSettings } from '@/entities/settings';
import type { ConversationContentSearchOptions } from '@/features/smart-search';
import type { ContentSearchTab } from '../ContentSearch/ContentSearchPanel';
import { isContentSearchColumnId } from './constants';
import { getSnippetColumnId } from './contentSearchColumnHelpers';

type ContentSearchActions = {
  setContentSearch: (value: TableContentSearchState | null) => void;
  onSortingChange: (
    next: TableSortingState | ((prev: TableSortingState) => TableSortingState),
  ) => void;
};

type UseContentSearchStateParams = {
  settings: Pick<TableSettings, 'contentSearch' | 'sorting' | 'search'>;
  actions: ContentSearchActions;
  /**
   * Триггер перезагрузки таблицы. Вызывается через useEffect после коммита React-рендера,
   * чтобы `fetchData`/`cacheVersion` потребителя успели обновиться к моменту перезапроса
   * (актуально для классической секции, использующей tableRef.reloadAll()).
   */
  onReload: () => void;
};

export type ContentSearchStateApi = {
  activeMode: ContentSearchMode | null;
  /**
   * Активный таб панели поиска. Вычисляется из `activeMode` и сессионного флага
   * видимости таба «атрибуты». `null` означает полностью свёрнутую панель.
   */
  activeTab: ContentSearchTab | null;
  committedValue: string;
  hasSearch: boolean;
  /**
   * Режим, чьи колонки выводятся в таблице; `null` — колонок поиска по содержимому нет.
   * Поиск по репликам показывает их только с непустым запросом, поиск по комментариям —
   * всё время активности режима (с пустым запросом колонка фрагмента показывает
   * последние комментарии, а «Релевантность» не выводится).
   */
  columnsMode: ContentSearchMode | null;
  inputValue: string;
  setInputValue: (value: string) => void;
  /** Выбранная сторона говорящего в режиме «по репликам»; `undefined` — «Все» (обе стороны) */
  phraseSearchRole: PhraseSearchRole | undefined;
  /** Смена стороны говорящего — управляющее действие уровня запроса (список перечитывается) */
  handlePhraseSearchRoleChange: (role: PhraseSearchRole | undefined) => void;
  /** Выбранные операторы в режиме «по репликам»; пустой массив — «Все» (все консультанты) */
  phraseSearchEmployeeIds: number[];
  /** Смена набора операторов — управляющее действие уровня запроса (список перечитывается) */
  handlePhraseSearchEmployeeIdsChange: (employeeIds: number[]) => void;
  /** Выбранные авторы комментариев в режиме «по комментариям»; пустой массив — «Все» (все авторы) */
  commentAuthorIds: number[];
  /** Смена набора авторов комментариев — управляющее действие уровня запроса (список перечитывается) */
  handleCommentAuthorIdsChange: (authorIds: number[]) => void;
  /**
   * Сброс поиска по содержимому к исходному состоянию: снимает активный контентный режим
   * вместе с выбранными авторами комментариев, стороной говорящего и набором операторов
   * (выбор возвращается к «Все»). Используется управляющим сбросом фильтров.
   */
  reset: () => void;
  /**
   * Очистка поля поиска по содержимому кнопкой-крестиком внутри самого поля.
   * В отличие от {@link reset}, панель не сворачивается — активный режим сохраняется,
   * но его поиск сбрасывается целиком: введённый и уже применённый фрагмент очищаются,
   * а донастройка запроса (сторона говорящего и набор операторов в режиме «по репликам»,
   * выбранные авторы в режиме «по комментариям») возвращается к «Все».
   */
  clearInput: () => void;
  contentSearchOptions: ConversationContentSearchOptions | undefined;
  effectiveSorting: TableSortingState;
  /** Переключение таба панели. `null` — деактивация (свёрнутое состояние). */
  handleTabChange: (tab: ContentSearchTab | null) => void;
  handleSubmit: (nextValue: string) => void;
  handleSortingChange: (
    next: TableSortingState | ((prev: TableSortingState) => TableSortingState),
  ) => void;
  /** Возвращает только пользовательскую часть columnOrder (без id колонок поиска по содержимому) */
  filterUserColumnOrder: (columnOrder: string[]) => string[];
  /** Возвращает итоговый порядок колонок с парой «Релевантность + фрагмент» в начале при активном режиме */
  getEffectiveColumnOrder: (userColumnOrder: string[]) => string[];
};

/**
 * Управляет состоянием поиска по содержимому диалога (spec: 03 IL/Переключатели поиска по содержимому диалога).
 *
 * Активный режим и подтверждённое значение хранятся в общем механизме настроек таблицы (localStorage)
 * вместе с колонками/сортировкой — это автоматически сохраняет состояние при переходе в карточку диалога
 * и обратно. Способ перезагрузки данных передаётся через `onReload`.
 */
export const useContentSearchState = ({
  settings,
  actions,
  onReload,
}: UseContentSearchStateParams): ContentSearchStateApi => {
  const persistedContentSearch = settings.contentSearch ?? null;
  const activeMode: ContentSearchMode | null = persistedContentSearch?.mode ?? null;
  const committedValue = persistedContentSearch?.value ?? '';
  // Сторона говорящего осмысленна только в режиме «по репликам».
  const committedRole = activeMode === 'phrase' ? persistedContentSearch?.role : undefined;
  // Набор авторов осмыслен только в режиме «по комментариям»; пустой массив — «Все».
  const committedAuthorIds = useMemo(
    () => (activeMode === 'comment' ? (persistedContentSearch?.commentAuthorIds ?? []) : []),
    [activeMode, persistedContentSearch?.commentAuthorIds],
  );
  // Стабильный ключ набора авторов для зависимостей эффектов/мемо (массив пересоздаётся каждый рендер).
  const committedAuthorIdsKey = committedAuthorIds.join(',');

  // Набор операторов осмыслен только в режиме «по репликам»; пустой массив — «Все». Не зависит от
  // выбранной стороны говорящего: выбор сохраняется при смене говорящего, но в запрос уходит только
  // при `role === 'employee'`.
  const committedEmployeeIds = useMemo(
    () => (activeMode === 'phrase' ? (persistedContentSearch?.phraseSearchEmployeeIds ?? []) : []),
    [activeMode, persistedContentSearch?.phraseSearchEmployeeIds],
  );
  // Стабильный ключ набора операторов для зависимостей эффектов/мемо.
  const committedEmployeeIdsKey = committedEmployeeIds.join(',');

  const [inputValue, setInputValue] = useState(committedValue);

  // Синхронизация инпута при внешних изменениях persisted-состояния (восстановление, переключение режима)
  useEffect(() => {
    setInputValue(committedValue);
  }, [committedValue, activeMode]);

  const hasSearch = committedValue.trim().length > 0;

  // Колонки поиска по содержимому: поиск по репликам без запроса ничего не отбирает и колонок не
  // даёт, а поиск по комментариям активен и с пустым запросом — там колонка фрагмента показывает
  // последний комментарий диалога.
  const columnsMode: ContentSearchMode | null = activeMode === 'comment' || hasSearch ? activeMode : null;

  // Сортировка по умолчанию при активном поиске — relevance DESC. Признак того, что пользователь
  // явно выбрал другую сортировку, хранится в персистируемом состоянии поиска (а не в эфемерном
  // состоянии секции) — поэтому уход в карточку диалога и возврат на список не считаются новой
  // активацией и не возвращают релевантность. Сброс признака происходит вместе со сменой
  // режима/значения: handleTabChange/handleSubmit создают новый contentSearch без флага.
  const userPickedSortInContentSearch = persistedContentSearch?.isSortingPicked ?? false;

  const effectiveSorting = useMemo<TableSortingState>(() => {
    if (hasSearch && !userPickedSortInContentSearch) {
      return [{ id: 'relevance', desc: true }];
    }
    const persisted = settings.sorting ?? [];
    // Поле `relevance` рассчитывается бэкендом только при активном поиске по содержимому
    // (phraseSearch/commentSearch). При переключении таба значение нового режима пустое —
    // активного поиска нет, а сохранённая ранее сортировка по relevance остаётся и уходит
    // в запрос, вызывая ошибку «Недопустимое поле сортировки 'relevance' для типа 'conversation'».
    // Поэтому без активного поиска отбрасываем relevance из сортировки.
    if (!hasSearch) {
      return persisted.filter((sort) => sort.id !== 'relevance');
    }
    return persisted;
  }, [hasSearch, userPickedSortInContentSearch, settings.sorting]);

  const contentSearchOptions = useMemo<ConversationContentSearchOptions | undefined>(() => {
    if (activeMode === 'phrase')
      return {
        phraseSearch: committedValue,
        phraseSearchRole: committedRole,
        // Ограничение по операторам осмысленно только при говорящем «Консультант».
        ...(committedRole === 'employee' && committedEmployeeIds.length
          ? { phraseSearchEmployeeIds: committedEmployeeIds }
          : {}),
      };
    if (activeMode === 'comment')
      return {
        commentSearch: committedValue,
        // Признак активного режима уходит и с пустым запросом — список тогда показывает
        // диалоги с комментариями, а не остаётся прежним.
        commentSearchActive: true,
        ...(committedAuthorIds.length ? { commentSearchAuthorIds: committedAuthorIds } : {}),
      };
    return undefined;
    // *IdsKey отражают изменение наборов без пересоздания массивов-зависимостей.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, committedValue, committedRole, committedAuthorIdsKey, committedEmployeeIdsKey]);

  // Перезагрузка после коммита React-рендера, когда fetchData/cacheVersion уже отражают новое состояние.
  // Решает stale-closure при `tableRef.current?.reloadAll()` из обработчика — раньше вызов попадал
  // на старое замыкание `fetchData` и таблица перезапрашивалась с прежними `contentSearchOptions`.
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    onReload();
    // onReload намеренно не в зависимостях — следим только за изменением committed-состояния
    // (режим, значение, сторона говорящего, набор операторов, набор авторов комментариев)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, committedValue, committedRole, committedEmployeeIdsKey, committedAuthorIdsKey]);

  // Видимость таба «Поиск по атрибутам» при свёрнутом контентном режиме.
  // «Поиск по атрибутам» — это основная строка поиска (DSL/NL), показываемая через SmartSearchInput.
  // По умолчанию блок поиска свёрнут — ни один режим не выбран. Если при возврате на список
  // восстановлен активный поиск по атрибутам (persisted `settings.search`), таб раскрыт, чтобы
  // показать сохранённый запрос. Клик по активному табу скрывает инпут и возвращает к свёрнутому виду.
  const hasPersistedAttributeSearch = (settings.search?.query ?? '').trim().length > 0;
  const [isAttributeTabActive, setIsAttributeTabActive] = useState(hasPersistedAttributeSearch);

  const activeTab: ContentSearchTab | null = useMemo(() => {
    if (activeMode === 'phrase' || activeMode === 'comment') return activeMode;
    return isAttributeTabActive ? 'attribute' : null;
  }, [activeMode, isAttributeTabActive]);

  const handleTabChange = useCallback(
    (tab: ContentSearchTab | null) => {
      if (tab === 'phrase' || tab === 'comment') {
        if (tab !== activeMode) {
          // Атомарный переход между контентными режимами — значение нового режима пустое
          actions.setContentSearch({ mode: tab, value: '' });
        }
        setIsAttributeTabActive(false);
      } else if (tab === 'attribute') {
        if (activeMode != null) actions.setContentSearch(null);
        setIsAttributeTabActive(true);
      } else {
        // tab === null — полная деактивация панели
        if (activeMode != null) actions.setContentSearch(null);
        setIsAttributeTabActive(false);
      }
      setInputValue('');
    },
    [actions, activeMode],
  );

  const handleSubmit = useCallback(
    (nextValue: string) => {
      if (activeMode == null) return;
      setInputValue(nextValue);
      // Подтверждение значения — новый сеанс поиска (сбрасывает isSortingPicked), но сохраняет
      // сторону говорящего и набор операторов (режим «по репликам») и набор авторов (режим «по комментариям»).
      const role = activeMode === 'phrase' ? persistedContentSearch?.role : undefined;
      const phraseSearchEmployeeIds =
        activeMode === 'phrase' ? persistedContentSearch?.phraseSearchEmployeeIds : undefined;
      const commentAuthorIds = activeMode === 'comment' ? persistedContentSearch?.commentAuthorIds : undefined;
      actions.setContentSearch({
        mode: activeMode,
        value: nextValue,
        ...(role ? { role } : {}),
        ...(phraseSearchEmployeeIds?.length ? { phraseSearchEmployeeIds } : {}),
        ...(commentAuthorIds?.length ? { commentAuthorIds } : {}),
      });
    },
    [
      actions,
      activeMode,
      persistedContentSearch?.role,
      persistedContentSearch?.phraseSearchEmployeeIds,
      persistedContentSearch?.commentAuthorIds,
    ],
  );

  const handlePhraseSearchRoleChange = useCallback(
    (role: PhraseSearchRole | undefined) => {
      // Роль относится только к режиму «по репликам»; меняем сторону, сохраняя режим/значение/сортировку.
      if (activeMode !== 'phrase' || persistedContentSearch == null) return;
      actions.setContentSearch({ ...persistedContentSearch, role });
    },
    [actions, activeMode, persistedContentSearch],
  );

  const handlePhraseSearchEmployeeIdsChange = useCallback(
    (employeeIds: number[]) => {
      // Набор операторов относится только к режиму «по репликам»; меняем его, сохраняя
      // режим/значение/сторону/сортировку. Пустой набор — «Все»: свойство не храним.
      if (activeMode !== 'phrase' || persistedContentSearch == null) return;
      actions.setContentSearch({
        ...persistedContentSearch,
        phraseSearchEmployeeIds: employeeIds.length ? employeeIds : undefined,
      });
    },
    [actions, activeMode, persistedContentSearch],
  );

  const handleCommentAuthorIdsChange = useCallback(
    (authorIds: number[]) => {
      // Набор авторов относится только к режиму «по комментариям»; меняем его, сохраняя
      // режим/значение/сортировку. Пустой набор — «Все»: свойство не храним.
      if (activeMode !== 'comment' || persistedContentSearch == null) return;
      actions.setContentSearch({
        ...persistedContentSearch,
        commentAuthorIds: authorIds.length ? authorIds : undefined,
      });
    },
    [actions, activeMode, persistedContentSearch],
  );

  const reset = useCallback(() => {
    // Снятие активного контентного режима очищает и выбранных авторов, и сторону говорящего:
    // пустой набор авторов трактуется как «Все», поэтому следующий поиск не остаётся сужен.
    if (activeMode != null) actions.setContentSearch(null);
    setInputValue('');
  }, [actions, activeMode]);

  const clearInput = useCallback(() => {
    // Крестик очистки поля сбрасывает поиск текущего режима, не сворачивая панель: новый пустой
    // сеанс того же режима без role/commentAuthorIds — значение и донастройка (сторона/авторы)
    // сбрасываются к «Все», чтобы следующий поиск не остался сужен прежней стороной/авторами.
    if (activeMode == null) return;
    actions.setContentSearch({ mode: activeMode, value: '' });
    setInputValue('');
  }, [actions, activeMode]);

  const handleSortingChange = useCallback(
    (next: TableSortingState | ((prev: TableSortingState) => TableSortingState)) => {
      // Фиксируем явный выбор сортировки в персистируемом состоянии поиска, чтобы он пережил
      // уход в карточку диалога и возврат на список (иначе восстановился бы дефолт relevance).
      if (hasSearch && persistedContentSearch && !persistedContentSearch.isSortingPicked) {
        actions.setContentSearch({ ...persistedContentSearch, isSortingPicked: true });
      }
      actions.onSortingChange(next);
    },
    [actions, hasSearch, persistedContentSearch],
  );

  const filterUserColumnOrder = useCallback(
    (columnOrder: string[]) => columnOrder.filter((id) => !isContentSearchColumnId(id)),
    [],
  );

  const getEffectiveColumnOrder = useCallback(
    (userColumnOrder: string[]) => {
      if (columnsMode == null) return userColumnOrder;
      const snippetId = getSnippetColumnId(columnsMode);
      // Без запроса релевантности нет — в начало идёт только колонка фрагмента.
      if (!hasSearch) return [snippetId, ...userColumnOrder];
      return ['relevance', snippetId, ...userColumnOrder];
    },
    [columnsMode, hasSearch],
  );

  return {
    activeMode,
    activeTab,
    committedValue,
    hasSearch,
    columnsMode,
    inputValue,
    setInputValue,
    phraseSearchRole: committedRole,
    handlePhraseSearchRoleChange,
    phraseSearchEmployeeIds: committedEmployeeIds,
    handlePhraseSearchEmployeeIdsChange,
    commentAuthorIds: committedAuthorIds,
    handleCommentAuthorIdsChange,
    reset,
    clearInput,
    contentSearchOptions,
    effectiveSorting,
    handleTabChange,
    handleSubmit,
    handleSortingChange,
    filterUserColumnOrder,
    getEffectiveColumnOrder,
  };
};
